import React from "react"
import { useTranslation } from "react-i18next"

import { DateRangeDisplay } from "@/components/date/dateRangeDisplay/dateRangeDisplay"
import {
  useFormatChargeRate,
  useFormatCurrency,
  useFormatDecimal,
} from "@/formatters"
import type {
  FormatChargeRate,
  FormatCurrency,
  FormatDecimal,
} from "@/formatters"
import { FONT_WEIGHT } from "@/models/typography"
import { HStack } from "@/nzds/layout"
import { PERIOD_DATE_FORMAT } from "@/utils/date"
import { hasText } from "@/utils/string"

import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"

import type { BillChargeGroup, BillChargeLine } from "./group-bill-charge-lines"

type BillChargeColumn = "amount" | "charge" | "period" | "rate" | "usage"

const hasLineUsage = (line: BillChargeLine): boolean =>
  hasText(line.quantity) || hasText(line.unit)

const hasLineRate = (line: BillChargeLine): boolean => hasText(line.rate)

const hasLinePeriod = (line: BillChargeLine): boolean =>
  hasText(line.service_start) || hasText(line.service_end)

interface BillChargeColumnSpec {
  align: "left" | "right"
  labelKey: string
  width?: string
}

// Fixed widths keep the same field in the same place from group to group.
// Charge takes the remaining width.
const CHARGE_COLUMN_SPECS: Record<BillChargeColumn, BillChargeColumnSpec> = {
  amount: {
    align: "right",
    labelKey: "billCharges.columns.amount",
    width: "14%",
  },
  charge: {
    align: "left",
    labelKey: "billCharges.columns.charge",
  },
  rate: {
    align: "right",
    labelKey: "billCharges.columns.rate",
    width: "14%",
  },
  period: {
    align: "left",
    labelKey: "fields.period.title",
    width: "22%",
  },
  usage: {
    align: "right",
    labelKey: "billCharges.columns.quantity",
    width: "16%",
  },
}

// Charge and amount are always shown. The other columns appear when any line
// in the section has a value, in the same order for every group.
export const buildBillChargeColumns = (
  lines: readonly BillChargeLine[]
): BillChargeColumn[] => {
  const columns: BillChargeColumn[] = ["charge"]

  if (lines.some((line) => hasLinePeriod(line))) {
    columns.push("period")
  }

  if (lines.some((line) => hasLineUsage(line))) {
    columns.push("usage")
  }

  if (lines.some((line) => hasLineRate(line))) {
    columns.push("rate")
  }

  columns.push("amount")

  return columns
}

// Usage and rate labels appear when any row in the group has that value.
// The column stays either way, so amounts still line up.
const getColumnHeaderLabelKey = (
  column: BillChargeColumn,
  lines: readonly BillChargeLine[]
): string | null => {
  switch (column) {
    case "amount":
    case "charge":
    case "period":
      return CHARGE_COLUMN_SPECS[column].labelKey
    case "rate":
      return lines.some((line) => hasLineRate(line))
        ? CHARGE_COLUMN_SPECS.rate.labelKey
        : null
    case "usage":
      return lines.some((line) => hasLineUsage(line))
        ? CHARGE_COLUMN_SPECS.usage.labelKey
        : null
    default: {
      const exhaustive: never = column
      return exhaustive
    }
  }
}

interface ChargeCellValueArgs {
  column: BillChargeColumn
  formatChargeRate: FormatChargeRate
  formatCurrency: FormatCurrency
  formatDecimal: FormatDecimal
  line: BillChargeLine
}

const formatChargeCell = ({
  column,
  formatChargeRate,
  formatCurrency,
  formatDecimal,
  line,
}: ChargeCellValueArgs): React.ReactNode => {
  switch (column) {
    case "amount":
      return formatCurrency(line.amount, { currency: line.currency })
    case "charge":
      return line.label
    case "rate":
      return formatChargeRate(line.rate, line.unit, line.currency)
    case "period":
      return (
        <DateRangeDisplay
          end={line.service_end}
          format={PERIOD_DATE_FORMAT}
          showPlaceholder={false}
          start={line.service_start}
        />
      )
    case "usage": {
      const quantity = hasText(line.quantity)
        ? formatDecimal(line.quantity)
        : null
      const unit = hasText(line.unit) ? line.unit : null

      if (quantity != null && unit != null) {
        return `${quantity} ${unit}`
      }

      return quantity ?? unit ?? ""
    }
    default: {
      const exhaustive: never = column
      return exhaustive
    }
  }
}

interface BillChargeGroupTableProps {
  columns: BillChargeColumn[]
  group: BillChargeGroup
}

export const BillChargeGroupTable: React.FC<BillChargeGroupTableProps> = ({
  columns,
  group,
}) => {
  const { t } = useTranslation()
  const { formatCurrency } = useFormatCurrency(
    {},
    { useAccountingFormatWhenNegative: false }
  )
  const { formatChargeRate } = useFormatChargeRate()
  const { formatDecimal } = useFormatDecimal(
    { precision: 2 },
    { useAccountingFormatWhenNegative: false }
  )
  const title = group.isMiscellaneous
    ? t("billCharges.miscellaneous")
    : group.title

  return (
    <TableContainer component={Paper} variant="outlined">
      <HStack
        sx={{
          alignItems: "baseline",
          bgcolor: "action.hover",
          borderBottom: 1,
          borderColor: "divider",
          gap: 2,
          justifyContent: "space-between",
          // Match the amount cell padding so the group total sits on that column.
          px: 2,
          py: 1,
        }}
      >
        <Typography
          component="h4"
          sx={{ fontWeight: FONT_WEIGHT.bold }}
          variant="subtitle1"
        >
          {title}
        </Typography>
        <Typography
          component="span"
          sx={{ fontWeight: FONT_WEIGHT.bold }}
          variant="subtitle1"
        >
          {formatCurrency(group.total, { currency: group.currency })}
        </Typography>
      </HStack>
      <Table
        aria-label={title}
        size="small"
        sx={{
          tableLayout: "fixed",
          width: "100%",
          "& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root":
            {
              borderBottom: 0,
            },
        }}
      >
        <colgroup>
          {columns.map((column) => (
            <col
              key={column}
              style={{ width: CHARGE_COLUMN_SPECS[column].width }}
            />
          ))}
        </colgroup>
        <TableHead>
          <TableRow>
            {columns.map((column) => {
              const labelKey = getColumnHeaderLabelKey(column, group.lines)

              return (
                <TableCell
                  align={CHARGE_COLUMN_SPECS[column].align}
                  key={column}
                  scope="col"
                >
                  {labelKey == null ? null : t(labelKey)}
                </TableCell>
              )
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {group.lines.map((line) => (
            <TableRow key={line.id}>
              {columns.map((column) => (
                <TableCell
                  align={CHARGE_COLUMN_SPECS[column].align}
                  key={column}
                >
                  {formatChargeCell({
                    column,
                    formatChargeRate,
                    formatCurrency,
                    formatDecimal,
                    line,
                  })}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
